import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { fetchPublicEnv } from './config/runtimeEnv';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PlanProvider } from './contexts/PlanContext';
import { RoleProvider } from './contexts/RoleContext';
import { isAdminRole, isSuperAdmin } from './constants/roles';
import RequireFeature from './components/RequireFeature';
import { FEATURES } from './constants/plans';
import { GameProvider } from './contexts/GameContext';
import { PlayerCacheProvider } from './contexts/PlayerCacheContext';
import { MatchCacheProvider } from './contexts/MatchCacheContext';
import { AlertProvider } from './contexts/AlertContext';
import { ConflictProvider } from './contexts/ConflictContext';
import { OfflineQueueProvider } from './contexts/OfflineQueueContext';
import { ConsentProvider } from './contexts/ConsentContext';
import ConflictModalRenderer from './components/ConflictModalRenderer';
import ConsentGate from './components/ConsentGate';
import { StatsModalProvider } from './contexts/StatsModalContext';
import { PageTitleProvider } from './contexts/PageTitleContext';
import { LeaderboardCelebrationProvider } from './contexts/LeaderboardCelebrationContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import StatsModalRenderer from './components/StatsModalRenderer';
import FloatingShareButton from './components/FloatingShareButton';
import AnalyticsTracker from './components/AnalyticsTracker';
import DataFetchOverlay from './components/DataFetchOverlay';
import AppLayout from './components/AppLayout';
import Landing from './pages/Landing';
import Onboarding from './pages/Onboarding';
import HomeFeed from './pages/HomeFeed';
import Dashboard from './pages/Dashboard';
import TournamentRegistration from './pages/TournamentRegistration';
import TournamentManagement from './pages/TournamentManagement';
import TournamentSchedule from './pages/TournamentSchedule';
import AdminTools from './pages/AdminTools';
import MatchSchedule from './pages/MatchSchedule';
import MatchScoring from './pages/MatchScoring';
import Leaderboard from './pages/Leaderboard';
import TournamentDashboard from './pages/TournamentDashboard';
import TournamentList from './pages/TournamentList';
import AdminPanel from './pages/AdminPanel';
import AdminViewAsUser from './pages/AdminViewAsUser';
import ApartmentManage from './pages/ApartmentManage';
import VenueManagement from './pages/VenueManagement';
import AuditLog from './pages/AuditLog';
import HostMatch from './pages/HostMatch';
import MatchHistory from './pages/MatchHistory';
import SelectApartment from './pages/SelectApartment';
import SelectGroup from './pages/SelectGroup';
import GroupManage from './pages/GroupManage';
import ProfileEdit from './pages/ProfileEdit';
import ProfileEditHistory from './pages/ProfileEditHistory';
import UserProfile from './pages/UserProfile';
import TeamStats from './pages/TeamStats';
import Subscription from './pages/Subscription';
import Privacy from './pages/Privacy';
import Statistics from './pages/Statistics';
import Widget from './pages/Widget';
import PublicSportPage from './pages/public/PublicSportPage';
import PublicMatchListPage from './pages/public/PublicMatchListPage';
import PublicMatchPage from './pages/public/PublicMatchPage';
import PublicPlayerPage from './pages/public/PublicPlayerPage';
import PublicTournamentPage from './pages/public/PublicTournamentPage';
import PublicTournamentMatchList from './pages/public/PublicTournamentMatchList';
import PublicTournamentMatchPage from './pages/public/PublicTournamentMatchPage';
import './App.css';

/** Protects routes that require login. Redirects to / (Landing) with returnTo so user returns after sign-in. */
const PrivateRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <DataFetchOverlay message="Loading…" />;
  }

  if (!currentUser) {
    const returnTo = (location.pathname || '') + (location.search || '');
    return <Navigate to="/" replace state={{ returnTo: returnTo || undefined }} />;
  }

  return children;
};

/** Protects admin-only routes. Requires login (via parent) and admin or super-admin role; redirects to /dashboard otherwise. */
const RequireAdmin = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();

  if (loading) {
    return <DataFetchOverlay message="Loading…" />;
  }

  const profileWithEmail = userProfile ? { ...userProfile, email: userProfile.email || currentUser?.email } : null;
  const hasAccess = profileWithEmail && (isAdminRole(profileWithEmail) || isSuperAdmin(profileWithEmail));
  if (!userProfile || !hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/** Protects super-admin-only routes (e.g. audit log). Requires login and super-admin role; redirects to /dashboard otherwise. */
const RequireSuperAdmin = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();

  if (loading) {
    return <DataFetchOverlay message="Loading…" />;
  }

  const profileWithEmail = userProfile ? { ...userProfile, email: userProfile.email || currentUser?.email } : null;
  const hasAccess = profileWithEmail && isSuperAdmin(profileWithEmail);
  if (!userProfile || !hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/** At "/" when not logged in show Landing; otherwise require auth and show app. */
function RootOrLanding() {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const pathname = location.pathname || '';

  if (loading) return <DataFetchOverlay message="Loading…" />;
  if (!currentUser) {
    if (pathname === '/' || pathname === '') return <Landing />;
    const returnTo = pathname + (location.search || '');
    return <Navigate to="/" state={{ returnTo }} replace />;
  }
  return (
    <PrivateRoute>
      <AppLayout />
    </PrivateRoute>
  );
}

function App() {
  useEffect(() => {
    fetchPublicEnv();
  }, []);

  // Defensive: ensure providers/components are defined (helps with "Element type is invalid: got: undefined")
  const providers = [
    ['AuthProvider', AuthProvider],
    ['PlanProvider', PlanProvider],
    ['RoleProvider', RoleProvider],
    ['GameProvider', GameProvider],
    ['PlayerCacheProvider', PlayerCacheProvider],
    ['MatchCacheProvider', MatchCacheProvider],
    ['AlertProvider', AlertProvider],
    ['ConflictProvider', ConflictProvider],
    ['OfflineQueueProvider', OfflineQueueProvider],
    ['ConflictModalRenderer', ConflictModalRenderer],
    ['Router', Router],
  ];
  const missing = providers.find(([, C]) => C === undefined);
  if (missing) {
    const [name] = missing;
    throw new Error(`App: "${name}" is undefined. Check import/export (default vs named) for that component.`);
  }

  return (
    <AuthProvider>
      <ImpersonationProvider>
      <PlanProvider>
        <RoleProvider>
          <GameProvider>
            <PlayerCacheProvider>
              <MatchCacheProvider>
                <AlertProvider>
                <ConflictProvider>
                <OfflineQueueProvider>
                <ConsentProvider>
                <ConflictModalRenderer />
                <StatsModalProvider>
                <Router>
                <LeaderboardCelebrationProvider>
                <PageTitleProvider>
                <ConsentGate>
                <AnalyticsTracker />
                <StatsModalRenderer />
              <div className="App">
            <FloatingShareButton />
            <Routes>
            {/* ========== PUBLIC (no login): shareable permalinks ========== */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/sport/:gameId" element={<PublicSportPage />} />
            <Route path="/sport/:gameId/match" element={<PublicMatchListPage />} />
            <Route path="/sport/:gameId/match/:matchId" element={<PublicMatchPage />} />
            <Route path="/player/:userId" element={<PublicPlayerPage />} />
            <Route path="/sport/:gameId/tournament/:tournamentId" element={<PublicTournamentPage />} />
            <Route path="/sport/:gameId/tournament/:tournamentId/match" element={<PublicTournamentMatchList />} />
            <Route path="/sport/:gameId/tournament/:tournamentId/match/:matchId" element={<PublicTournamentMatchPage />} />
            {/* ========== PROTECTED (login required) ========== */}
            <Route
              path="/onboarding"
              element={
                <PrivateRoute>
                  <Onboarding />
                </PrivateRoute>
              }
            />
            <Route path="/select-apartment" element={<PrivateRoute><SelectApartment /></PrivateRoute>} />
            {/* RootOrLanding: at "/" when not logged in → Landing; else redirect to /; when logged in → AppLayout with all nested routes protected */}
            {/* Widget: fullscreen when opened from Add to Home Screen (no app chrome) */}
            <Route path="/widget" element={<PrivateRoute><Widget /></PrivateRoute>} />
            <Route path="/*" element={<RootOrLanding />}>
              {/* All nested routes require login (enforced by RootOrLanding → PrivateRoute) */}
              <Route index element={<HomeFeed />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="tournament/register" element={<TournamentRegistration />} />
              <Route path="tournament/manage" element={<RequireFeature featureKey={FEATURES.TOURNAMENT_MANAGE}><TournamentManagement /></RequireFeature>} />
              <Route path="tournament/schedule" element={<RequireFeature featureKey={FEATURES.TOURNAMENT_SCHEDULE}><TournamentSchedule /></RequireFeature>} />
              <Route path="schedule" element={<MatchSchedule />} />
              <Route path="host-match" element={<RequireFeature featureKey={FEATURES.HOST_MATCH}><HostMatch /></RequireFeature>} />
              <Route path="match/:matchId" element={<MatchScoring />} />
              <Route path="leaderboard" element={<RequireFeature featureKey={FEATURES.LEADERBOARD}><Leaderboard /></RequireFeature>} />
              <Route path="statistics" element={<RequireFeature featureKey={FEATURES.STATISTICS}><Statistics /></RequireFeature>} />
              {/* Tournament playing/registered: always shown for any plan */}
              <Route path="tournament/:tournamentId/dashboard" element={<TournamentDashboard />} />
              <Route path="tournament/dashboard" element={<TournamentDashboard />} />
              <Route path="tournaments" element={<TournamentList />} />
              {/* Admin-only (login + admin role) */}
              <Route path="admin" element={<RequireAdmin><AdminPanel /></RequireAdmin>} />
              <Route path="admin/view-as/:userId" element={<RequireAdmin><AdminViewAsUser /></RequireAdmin>} />
              <Route path="admin/tools" element={<RequireAdmin><AdminTools /></RequireAdmin>} />
              <Route path="audit-log" element={<RequireSuperAdmin><AuditLog /></RequireSuperAdmin>} />
              <Route path="venues" element={<VenueManagement />} />
              <Route path="apartment-manage" element={<ApartmentManage />} />
              <Route path="select-group" element={<SelectGroup />} />
              <Route path="groups" element={<GroupManage />} />
              <Route path="profile/edit" element={<ProfileEdit />} />
              <Route path="profile/activity" element={<ProfileEditHistory />} />
              <Route path="profile/:userId" element={<UserProfile />} />
              <Route path="team-stats" element={<TeamStats />} />
              <Route path="subscription" element={<Subscription />} />
              {/* Match history for tournaments you're in: always shown for any plan */}
              <Route path="tournament/:tournamentId/matches" element={<MatchHistory />} />
            </Route>
            </Routes>
              </div>
                </ConsentGate>
                </PageTitleProvider>
                </LeaderboardCelebrationProvider>
            </Router>
                </StatsModalProvider>
                </ConsentProvider>
                </OfflineQueueProvider>
                </ConflictProvider>
                </AlertProvider>
              </MatchCacheProvider>
            </PlayerCacheProvider>
          </GameProvider>
        </RoleProvider>
      </PlanProvider>
      </ImpersonationProvider>
    </AuthProvider>
  );
}

export default App;

